import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { LogService } from 'src/app/services/log.service';
import { TankService } from 'src/app/services/tank.service';
import { DropDown } from 'src/app/interfaces/drop-down.interface';
import { DropDownTreeComponent } from '@syncfusion/ej2-angular-dropdowns';


export const TANK_FORM_CONTROL_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MultiSelectTankFormFieldComponent),
  multi: true,
};

interface TreeNode {
  id: string;
  pid: string | null;
  text: string;
  hasChildren?: boolean;
  expanded?: boolean;
  selectable?: boolean;
  value?: string;
  child?: TreeNode[];
  disabled?: boolean;
}

@Component({
  selector: 'app-multi-select-tank-form-field',
  providers: [TANK_FORM_CONTROL_ACCESSOR],
  templateUrl: './multi-select-tank-form-field.component.html',
  styleUrls: ['./multi-select-tank-form-field.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiSelectTankFormFieldComponent
  implements ControlValueAccessor, OnChanges, AfterViewInit {

  @ViewChild('tankDropDownTree') dropDownTree: DropDownTreeComponent;
  @ViewChild('tankTreeWrapper', { read: ElementRef }) tankTreeElementRef: ElementRef;

  @Input() locationId: number[] = [];
  @Input() disabledTankIds: number[] = [];

  options: DropDown<string>[] = null;
  fields: Object = { value: 'value', text: 'text' };
  loading: boolean = false;
  _value: DropDown<string>[] = [];
  previousLocationIds: number[] = [];
  private dataLoaded = false;
  private selectedTankSet = new Set<string>(); // <-- the reliable selection
  public enabledSelectedCount = 0;


  getEnabledSelectedCount(): number {
    return this.selectedTankIds.filter(id => !this.isReadonly(id)).length;
  }


  public treeData: TreeNode[] = [];

  get treeFields(): any {
    return {
      dataSource: this.treeData,
      id: 'id',
      parentValue: 'pid',
      text: 'text',
      hasChildren: 'hasChildren',
      value: 'value',
      disabled: 'disabled'
    };
  }

  public watermark: string = 'Select a Tank';
  public showCheckBox = true;
  public mode = "Custom";
  public treeSettings: Object = {
    autoCheck: false,
    loadOnDemand: false, checkDisabledChildren: false
  };
  public filterType: string = 'Contains';


  public selectedTankIds: string[] = [];
  public tankNodeIds: string[] = [];

  private _disabled: boolean = false;
  public get disabled(): boolean {
    return this._disabled || this.loading;
  }
  @Input() public set disabled(value: boolean) {
    this._disabled = value;
  }

  constructor(
    private _ts: TankService,
    private _lgr: LogService,
    private _cdr: ChangeDetectorRef,
  ) { }


  ngAfterViewInit(): void {
    this.updateTankTreeValue();
  }

  // Function to call when the input changes.
  onChange = (value: DropDown<string>[] | null) => { };

  // Function to call when the input is touched.
  onTouched = () => { };

  writeValue(value: DropDown<string>[]): void {
    if (value) {
      this.selectedTankIds = value.map(v => `tank_${v.value}`);
      this.selectedTankSet = new Set(this.selectedTankIds);
    } else {
      this.selectedTankIds = [];
      this.selectedTankSet = new Set();
    }
    this.updateEnabledSelectedCount();
    this._cdr.markForCheck();
    if (this.treeData?.length) {
      this.updateTankTreeValue();
    }
    // ✅ Force refresh of the customTemplate in input
    setTimeout(() => {
      this.dropDownTree?.dataBind();
      (this.dropDownTree as any).updateInput?.(true); // <== Key line
      (this.dropDownTree as any).refreshInput?.(); // New
      this.updateDisplayText();
    }, 0);
    //this.updateDisplayText();
  }

  // Allows Angular to register a function to call when the model (organizationControl) changes.
  // Save the function as a property to call later here.
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // Allows Angular to register a function to call when the input has been touched.
  // Save the function as a property to call later here.
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this._cdr.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['locationId']) {
      const currentIds = this.locationId || [];
      if (!currentIds.length || this.arraysEqual(this.previousLocationIds, currentIds)) {
        return;
      }
      this.previousLocationIds = [...currentIds];
      //this.selectedTankIds = [];
      this.treeData = [];
      this.tankNodeIds = [];
      this.loadTankData();
    }

  }

  private buildFlatTree(data: any[]): TreeNode[] {
    const flat: TreeNode[] = [];

    data.forEach((item) => {
      const orgId = `org_${item.orgId}`;
      const locId = `loc_${item.locationId}`;
      const tankId = `tank_${item.tankId}`;

      if (!flat.find(n => n.id === orgId)) {
        flat.push({ id: orgId, pid: null, text: item.orgName, hasChildren: true, value: orgId, expanded: true, selectable: true });
      }

      if (!flat.find(n => n.id === locId)) {
        flat.push({ id: locId, pid: orgId, text: item.locationName, hasChildren: true, value: locId, expanded: true, selectable: true });
      }

      flat.push({
        id: tankId,
        pid: locId,
        text: item.tankName,
        hasChildren: false,
        selectable: true, // still needed so node renders
        value: tankId,
        disabled: this.disabledTankIds.includes(item.tankId),
        expanded: true
      });
    });

    return flat;
  }

  private arraysEqual(a: number[], b: number[]): boolean {
    if (a.length !== b.length) return false;
    return [...a].sort().every((val, idx) => val === [...b].sort()[idx]);
  }


  private loadTankData(): void {
    this.loading = true;
    this._cdr.markForCheck();

    // ✅ Step 1: Save currently selected location IDs
    const previouslySelected = [...this.selectedTankIds];

    this._ts.getTanksForDropDownByLocationIds(this.locationId).subscribe((tanks) => {
      this.treeData = this.buildFlatTree(tanks);
      this.dataLoaded = true;
      this.tankNodeIds = this.getAllTankNodeIds(this.treeData);
      this.loading = false;

      // ✅ Step 2: Filter out only those that still exist
      const newValidSelected = previouslySelected.filter(id =>
        this.tankNodeIds.includes(id)
      );

      this.selectedTankIds = [...newValidSelected];

      // ✅ Step 3: Update model (_value) based on filtered selected IDs
      this._value = this.getSelectedDropDownsFromIds(this.selectedTankIds);


      setTimeout(() => {
        this.updateTankTreeValue();
        this.onChange(this._value); // Update form value
        this.updateEnabledSelectedCount();
        this._cdr.markForCheck();
        //this.updateDisplayText();
        this._cdr.detectChanges();
      });
    });
  }

  private getAllTankNodeIds(data: any[]): string[] {
    return data
      .filter(node => node.selectable && node.id.startsWith('tank_'))
      .map(node => node.id);
  }

  private isParentNode(nodeId: string): boolean {
    return this.treeData.some(node => node.pid === nodeId);
  }
  private getChildNodeIds(parentId: string): string[] {
    return this.treeData
      .filter(node => node.pid === parentId && node.id.startsWith('tank_'))
      .map(node => node.id);
  }

  private getSelectedDropDownsFromIds(ids: string[]): DropDown<string>[] {
    const results: DropDown<string>[] = [];
    const nodesById = new Map(this.treeData.map((node) => [node.id, node]));

    const flatten = (nodes: any[]) => {
      for (const node of nodes) {
        if (node.selectable && ids.includes(node.id)) {
          const location = node.pid ? nodesById.get(node.pid) : null;
          const organization = location?.pid ? nodesById.get(location.pid) : null;
          results.push({
            text: node.text,
            value: node.id.replace('tank_', ''),
            parentValue: node.pid?.replace('loc_', ''),
            parentText: location?.text,
            rootValue: organization?.id.replace('org_', ''),
            rootText: organization?.text,
          });
        }
        if (node.child) {
          flatten(node.child);
        }
      }
    };

    flatten(this.treeData);
    return results;
  }

  private updateTankTreeValue(): void {
    if (this.dropDownTree) {
      this.dropDownTree.value = [...this.selectedTankIds];
      this.dropDownTree.dataBind();
      (this.dropDownTree as any).updateInput?.(true);
      (this.dropDownTree as any).refreshInput?.(); // New
      // Fallback: manually clear native input (in case Syncfusion caches it)
      const input: HTMLInputElement = this.tankTreeElementRef?.nativeElement.querySelector('input');
      if (input) {
        input.value = '';
      }
    }

    setTimeout(() => {
      this._cdr.detectChanges();
    }, 0);
  }

  private updateDisplayText(): void {
    const input: HTMLInputElement = this.tankTreeElementRef?.nativeElement.querySelector('input');

    if (!input) return;

    const enabledSelectedIds = this.selectedTankIds.filter(id => !this.isReadonly(id));
    const locationCount = enabledSelectedIds.length;

    const displayText = locationCount > 0
      ? `${locationCount} item${locationCount > 1 ? 's' : ''} selected`
      : '';

    input.value = displayText;
    input.setAttribute('value', displayText);

    // Also update the internal DropDownTree display (Syncfusion control)
    const dropDownTree = (this.dropDownTree as any);
    dropDownTree?.updateInput?.(true);
    dropDownTree?.refreshInput?.();

    this._cdr.detectChanges();
  }


  onDataBound(): void {
    setTimeout(() => {
      const treeViewObj = (this.dropDownTree as any).treeObj;
      if (!treeViewObj || !this.treeData?.length) return;

      const disabledSet = new Set<string>();

      // Step 1: Disable all tank nodes based on disabledTankIds
      const disabledTankNodeIds = this.treeData
        .filter(node =>
          node.id.startsWith('tank_') &&
          this.disabledTankIds.includes(Number(node.id.replace('tank_', '')))
        )
        .map(node => node.id);

      disabledTankNodeIds.forEach(id => disabledSet.add(id));

      // Step 2: Disable locations if all their child tanks are disabled
      const locationNodes = this.treeData.filter(node => node.id.startsWith('loc_'));
      for (const location of locationNodes) {
        const childTanks = this.treeData.filter(n => n.pid === location.id && n.id.startsWith('tank_'));
        if (childTanks.length && childTanks.every(child => disabledSet.has(child.id))) {
          disabledSet.add(location.id);
        }
      }

      // Step 3: Disable organizations if all their child locations are disabled
      const orgNodes = this.treeData.filter(node => node.id.startsWith('org_'));
      for (const org of orgNodes) {
        const childLocs = this.treeData.filter(n => n.pid === org.id && n.id.startsWith('loc_'));
        if (childLocs.length && childLocs.every(child => disabledSet.has(child.id))) {
          disabledSet.add(org.id);
        }
      }

      // ✅ Step 3: Build final checked set
      const selectedIdsFromWriteValue = (this._value ?? []).map(v => `tank_${v.value}`);
      const allValidIds = new Set(this.treeData.map(n => n.id));
      const manuallyIncludeDisabledIds = disabledTankNodeIds.filter(id => allValidIds.has(id));

      const finalCheckSet = new Set<string>([
        ...this.selectedTankIds,
        ...selectedIdsFromWriteValue,
        ...manuallyIncludeDisabledIds,
      ]);

      // Only keep valid node IDs from the tree
      const validCheckedNodes = [...finalCheckSet].filter(id =>
        this.treeData.some(node => node.id === id)
      );

      // Step 4: Apply checked nodes
      treeViewObj.checkedNodes = validCheckedNodes;
      treeViewObj.dataBind();

      // Step 5: Disable nodes
      if (treeViewObj.disableNodes && disabledSet.size > 0) {
        treeViewObj.disableNodes(Array.from(disabledSet));
      }

      this.selectedTankIds = validCheckedNodes.filter(id =>
        id.startsWith('tank_') &&
        !this.disabledTankIds.includes(Number(id.replace('tank_', '')))
      );
      this._cdr.detectChanges();
    });
  }

  onFiltering(event: any): void {
    // Optional: can customize filtering behavior if needed
    // For now, allow default filtering

    const treeViewObj = (this.dropDownTree as any)?.treeObj;

    if (treeViewObj) {
      // Reapply the already selected locations as checked
      treeViewObj.checkedNodes = [...this.selectedTankIds];
      treeViewObj.dataBind();
    }

    // Keep count correct
    this.updateDisplayText();
  }

  onInput(event: any): void {
    const inputValue = event?.target?.value?.trim() ?? '';

    if (!inputValue) {
      // Filter cleared – force checked node sync
      setTimeout(() => {
        const treeViewObj = (this.dropDownTree as any).treeObj;

        if (treeViewObj) {
          const currentChecked = treeViewObj.getAllCheckedNodes(); // <== KEY FIX

          // Reassign checked nodes to force re-render
          treeViewObj.checkedNodes = [...currentChecked];
          treeViewObj.dataBind();
        }
      });
    }
  }

  private handlingNodeSelect = false;

  onNodeSelect(event: any): void {
    // Writing `checkedNodes` raises `select` once per checked node, so a single
    // programmatic re-sync fired this handler ~350k times with 481 tanks selected.
    // Syncfusion flags those as not interacted; only a real click is ours to handle.
    if (!event?.isInteracted) return;
    if (this.handlingNodeSelect) return;
    this.handlingNodeSelect = true;

    try {
      const nodeData = event.itemData;
      const treeViewObj = (this.dropDownTree as any).treeObj;

      if (!nodeData || !treeViewObj) return;

      const nodeId = nodeData.id;
      const isChecked = nodeData.isChecked === 'true'; // syncfusion sends 'true' as string

      // Built once per event. These were array scans run per selected id below,
      // which made each pass cost selection x tree size.
      const disabledSet = new Set<number>(this.disabledTankIds ?? []);
      const treeNodeIds = new Set<string>(this.treeData.map(node => node.id));
      const isDisabledTank = (id: string) =>
        id.startsWith('tank_') && disabledSet.has(Number(id.replace('tank_', '')));

      const checkedSet = new Set<string>(treeViewObj.getAllCheckedNodes?.() ?? []);

      //  Step 1: Handle child propagation (downward)
      if (nodeData.hasChildren) {
        const childNodeIds = this.getAllDescendantNodeIds(nodeId);
        for (const childId of childNodeIds) {
          if (!isDisabledTank(childId)) {
            isChecked ? checkedSet.add(childId) : checkedSet.delete(childId);
          }
        }
      }

      //  Step 2: Toggle the current node
      if (isChecked) {
        checkedSet.add(nodeId);
      } else {
        checkedSet.delete(nodeId);
      }

      //  Step 3: Propagate to parent (upward)
      //this.updateParentNodesAfterChange(nodeId, checkedSet);
      this.updateParentSelection(nodeId, checkedSet);

      //  Step 4: Apply to tree view
      treeViewObj.checkedNodes = Array.from(checkedSet);
      treeViewObj.dataBind();

      //  Step 5: Sync selected tank IDs
      const allChecked = treeViewObj.getAllCheckedNodes?.() ?? [];
      const selectedIdsFromWriteValue = (this._value ?? []).map(v => `tank_${v.value}`);

      const finalCheckSet = new Set<string>([
        ...allChecked,
        ...this.selectedTankIds,
        ...selectedIdsFromWriteValue,
      ]);

      const validCheckedNodes = [...finalCheckSet].filter(id => treeNodeIds.has(id));

      this.selectedTankIds = validCheckedNodes.filter(
        id => id.startsWith('tank_') && !isDisabledTank(id)
      );

      if (!isChecked) {
        this.selectedTankIds = this.selectedTankIds.filter(id => id !== nodeId);

        if (nodeData.hasChildren) {
          const childNodeIds = new Set<string>(this.getAllDescendantNodeIds(nodeId));
          this.selectedTankIds = this.selectedTankIds.filter(
            id => !childNodeIds.has(id) || isDisabledTank(id)
          );
        }
      }

      this.selectedTankIds = [...this.selectedTankIds];
      this.selectedTankSet = new Set(this.selectedTankIds);

      const selectedValues: DropDown<string>[] =
        this.getSelectedDropDownsFromIds(this.selectedTankIds);
      this.onChange(selectedValues);
      this.onTouched();

      this.updateEnabledSelectedCount();

      //  Step 6: Refresh the display only. Step 4 already wrote the authoritative
      //  checked state; re-writing a narrower set here made the tree raise `select`
      //  again after the re-entrancy flag had been released, so the handler kept
      //  re-triggering itself, and it dropped the parent and disabled nodes that
      //  Steps 3-4 had just propagated.
      setTimeout(() => {
        this.updateDisplayText();
        treeViewObj.updateInput?.(true);
        treeViewObj.refreshInput?.();
        this._cdr.markForCheck();
        this._cdr.detectChanges();
      }, 10);

    } finally {
      this.handlingNodeSelect = false;
    }
  }


  private updateParentNodesAfterChange(nodeId: string, checkedSet: Set<string>): void {
    let currentId = nodeId;
    while (true) {
      const parentId = this.treeData.find(n => n.id === currentId)?.pid;
      if (!parentId) break;

      const siblings = this.treeData.filter(n => n.pid === parentId).map(n => n.id);

      const allSiblingsChecked = siblings.every(sib => checkedSet.has(sib));

      if (allSiblingsChecked) {
        checkedSet.add(parentId);
      } else {
        checkedSet.delete(parentId);
      }

      currentId = parentId;
    }

    //  Debug log at the end
    console.log(`Finished parent update for node: ${nodeId}`);
    console.log(`Final checkedSet:`, Array.from(checkedSet));
  }
   
  private getParentId(nodeId: string): string | null {
    const node = this.treeData.find(n => n.id === nodeId);
    return node?.pid ?? null;
  }
  // private updateParentSelection(nodeId: string, checkedSet: Set<string>): void {
  //   const parentId = this.treeData.find(n => n.id === nodeId)?.pid;
  //   if (!parentId) return;

  //   const childIds = this.getDirectChildrenIds(parentId);
  //   const checkedChildren = childIds.filter(id => checkedSet.has(id));

  //   console.log('checkedChildren', checkedChildren.length);
  //   console.log('childIds', childIds.length);

  //   if (checkedChildren.length === childIds.length) {
  //     //  All children checked → parent fully checked
  //     checkedSet.add(parentId);
  //     (this.dropDownTree as any).treeObj?.updateNode(parentId, { isChecked: true, isIndeterminate: false });
  //   } else if (checkedChildren.length === 0) {
  //     //  None checked → parent unchecked
  //     checkedSet.delete(parentId);
  //     (this.dropDownTree as any).treeObj?.updateNode(parentId, { isChecked: false, isIndeterminate: false });
  //   } else {
  //     //  Some children checked → parent indeterminate
  //     checkedSet.delete(parentId); // not a fully checked node
  //     (this.dropDownTree as any).treeObj?.updateNode(parentId, { isChecked: false, isIndeterminate: true });
  //   }

  //   //  Recurse upwards
  //   this.updateParentSelection(parentId, checkedSet);
  // }

 private updateParentSelection(nodeId: string, checkedSet: Set<string>): void {
  const parentId = this.treeData.find(n => n.id === nodeId)?.pid;
  if (!parentId) return;

  const childIds = this.getDirectChildrenIds(parentId);
  const checkedChildren = childIds.filter(id => checkedSet.has(id));

  const allChecked = checkedChildren.length === childIds.length;
  const noneChecked = checkedChildren.length === 0;

  const treeObj = (this.dropDownTree as any)?.treeObj;
  if (!treeObj || !treeObj.getNode) return;

  const parentNode = treeObj.getNode(parentId);
  if (!parentNode) return;

  const currentChecked = !!parentNode.isChecked;
  const currentIndeterminate = !!parentNode.isIndeterminate;

  let desiredState: { isChecked: boolean, isIndeterminate: boolean };

  if (allChecked) {
    desiredState = { isChecked: true, isIndeterminate: false };
    checkedSet.add(parentId);
  } else if (noneChecked) {
    desiredState = { isChecked: false, isIndeterminate: false };
    checkedSet.delete(parentId);
  } else {
    desiredState = { isChecked: false, isIndeterminate: true };
    checkedSet.delete(parentId); // not fully selected
  }

  // ✅ Skip updateNode if already in correct state
  if (
    desiredState.isChecked !== currentChecked ||
    desiredState.isIndeterminate !== currentIndeterminate
  ) {
    treeObj.updateNode(parentId, desiredState);
  }

  // ✅ Recurse upward regardless, because ancestor state may change due to sibling updates
  this.updateParentSelection(parentId, checkedSet);
}


  private getDirectChildrenIds(parentId: string): string[] {
    return this.treeData
      .filter(node => node.pid === parentId)
      .filter(node => {
        //  Tanks → check against disabledTankIds
        if (node.id.startsWith('tank_')) {
          const tankId = Number(node.id.replace('tank_', ''));
          return !this.disabledTankIds.includes(tankId);
        }
        //  Organizations / Locations → always enabled
        return true;
      })
      .map(node => node.id);
  }
  onTankDropDownOpen(): void {
    const treeViewObj = (this.dropDownTree as any)?.treeObj;
    if (!treeViewObj) return;

    // Re-sync tree checkboxes with selectedTankIds
    treeViewObj.checkedNodes = [...this.selectedTankIds];
    treeViewObj.dataBind();

    // Optional: refresh display
    setTimeout(() => {
      treeViewObj.updateInput?.(true);
      treeViewObj.refreshInput?.();
    }, 10);
  }
  private getAllDescendantNodeIds(parentId: string): string[] {
    const descendants: string[] = [];

    const walk = (pid: string) => {
      const children = this.treeData.filter(node => node.pid === pid);
      for (const child of children) {
        descendants.push(child.id);
        if (child.hasChildren) {
          walk(child.id);
        }
      }
    };

    walk(parentId);
    return descendants;
  }

  onCustomClear(): void {
    const ddTree = this.dropDownTree;
    const treeObj = (ddTree as any)?.treeObj;
    if (!treeObj) return;

    // Step 1: Clear all selections and checked nodes
    treeObj.clearCheckedNodes?.();
    treeObj.clearSelection?.();
    treeObj.value = [];
    treeObj.checkedNodes = [];
    treeObj.selectedNodes = [];

    treeObj.dataBind();
    // Step 2: Reset internal model
    this.selectedTankIds = [];
    this._value = [];

    // Step 3: Restore disabled tank selections
    const disabledChecked = this.treeData
      .filter(node =>
        node.id.startsWith('tank_') &&
        this.disabledTankIds.includes(Number(node.id.replace('tank_', '')))
      )
      .map(node => node.id);

    if (disabledChecked.length > 0) {
      treeObj.checkedNodes = disabledChecked;
      ddTree.value = disabledChecked;
      treeObj.dataBind();
      this.selectedTankIds = [...disabledChecked]; // So they're visible in the UI
    } else {
      treeObj.dataBind();
      ddTree.value = [];
    }

    // Step 4: Notify reactive form and force refresh
    const selectedValues: DropDown<string>[] = this.getSelectedDropDownsFromIds(this.selectedTankIds);
    this.onChange(selectedValues);
    this.onTouched();
    // Clear input field
    const input: HTMLInputElement = this.tankTreeElementRef?.nativeElement.querySelector('input');
    if (input) input.value = '';

    setTimeout(() => {
      this.restoreDisabledCheckedNodes(treeObj);
      ddTree.dataBind();
      this.updateEnabledSelectedCount();
      this.updateDisplayText();
      this._cdr.markForCheck();
      this._cdr.detectChanges();
    }, 0);
  }

  isReadonly(id: string): boolean {
    if (!id.startsWith('tank_')) return false;
    const numericId = Number(id.replace('tank_', ''));
    return this.disabledTankIds.includes(numericId);
  }

  private restoreDisabledCheckedNodes(treeObj: any): void {
    if (!this.disabledTankIds?.length) return;

    const disabledChecked = this.treeData
      .filter(node =>
        node.id.startsWith('tank_') &&
        this.disabledTankIds.includes(Number(node.id.replace('tank_', '')))
      )
      .map(node => node.id);

    // Restore disabled selections
    treeObj.checkedNodes = disabledChecked;
    treeObj.value = disabledChecked;
    treeObj.selectedNodes = disabledChecked;
    treeObj.dataBind();

    this._cdr.detectChanges();
  }

  private updateEnabledSelectedCount(): void {
    this.enabledSelectedCount = this.selectedTankIds.filter(id => !this.isReadonly(id)).length;
  }

}
